import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import { Text } from '@change/ds-core-ui/next';

import { Translate, TranslatePluralHtml } from '@change-corgi/core/react/i18n';

import { useSpeedometer } from './hooks/useSpeedometer';

const FULL_ARC_FRACTION = 2 / 3;

const animatePath = keyframes`
  0% {
    stroke-dasharray: 0 10;
  }
  100% {
    stroke-dasharray: ${2 * Math.PI * FULL_ARC_FRACTION} 10;
  }
`;

const TransitionPath = styled('path')`
	animation: ${animatePath} 3s ease-in-out forwards;
	fill: transparent;
	stroke-linecap: round;
	stroke: gradient-fill-progress;
`;

type Point = [number, number];
const fractionToRectangular = (fraction: number): Point => [
	Math.cos(fraction * 2 * Math.PI),
	Math.sin(fraction * 2 * Math.PI),
];

const createPathData = ({
	start: [startX, startY],
	largeArc,
	end: [endX, endY],
}: {
	start: Point;
	largeArc: boolean;
	end: Point;
}) => {
	const radius = 1;
	return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArc ? 1 : 0} 1 ${endX} ${endY}`;
};

const createPathDataForProgress = ({ progress }: { progress: number }) => {
	const startFraction = -1 / 4 - FULL_ARC_FRACTION / 2;
	const endFraction = startFraction + progress * FULL_ARC_FRACTION;

	const start = fractionToRectangular(startFraction);
	const end = fractionToRectangular(endFraction);

	const largeArc = endFraction - startFraction >= 0.5;

	return createPathData({ start, largeArc, end });
};

export function Speedometer() {
	const { data } = useSpeedometer();

	const strokeWidth = 0.16;

	return (
		<>
			<div className="sr-only">
				<label htmlFor="progress-bar">
					<Translate
						value="corgi.petition.starter_dashboard.petition_overview.a11y.progress_label"
						replacements={{ progress: data.progressPercentage }}
					/>
				</label>
				<progress id="progress-bar" value={data.progressPercentage} max="100">
					{data.progressPercentage}
				</progress>
				<Text>
					<TranslatePluralHtml
						value="corgi.petition.starter_dashboard.petition_overview.progress.supporters"
						count={data.supporters}
						replacements={{ supporterCount: data.supportersFormatted }}
					/>
				</Text>
			</div>
			<div className="relative grid w-full grid-cols-1 grid-rows-1" aria-hidden>
				<Text className="col-start-1 row-start-1 flex w-4/5 flex-col items-center justify-start gap-1 justify-self-center pb-[30px] pt-[60px] text-center [&>span:first-of-type]:mb-[-2px] [&>span:first-of-type]:text-size-heading-xl [&>span:first-of-type]:font-bold [&_br]:hidden">
					<TranslatePluralHtml
						value="corgi.petition.starter_dashboard.petition_overview.progress.supporters"
						count={data.supporters}
						replacements={{ supporterCount: data.supportersFormatted }}
					/>
				</Text>
				<div className="col-start-1 row-start-1 max-h-60 w-full max-w-60 justify-self-center">
					<svg
						id="my-svg"
						viewBox={`${-1 - strokeWidth / 2} ${-1 - strokeWidth / 2} ${2 + strokeWidth} ${1.5 + strokeWidth}`}
						className="block max-h-full max-w-full bg-transparent"
					>
						<defs>
							<linearGradient id="linear-grad" x1="0" y1="1" x2="1" y2="0">
								<stop offset="0.341346" stopColor="#0038BC" />
								<stop offset="0.754808" stopColor="#4771FF" />
								<stop offset="0.927885" stopColor="#4AAEFF" />
							</linearGradient>
						</defs>
						<path
							className="fill-transparent stroke-[var(--fill-weaker-color)]"
							strokeWidth={`${strokeWidth}px`}
							d={createPathDataForProgress({ progress: 1 })}
							strokeLinecap="round"
						/>
						<TransitionPath
							id="filled-path"
							d={createPathDataForProgress({ progress: data.progress })}
							strokeWidth={`${strokeWidth}px`}
							stroke="url(#linear-grad)"
						/>
						;
					</svg>
				</div>
			</div>
		</>
	);
}
